<template>
  <div class="test-drive-detail">
    <KTCodePreview
      v-bind:title="
        isEditMode ? 'Cập nhật đăng ký lái thử' : 'Thêm mới đăng ký lái thử'
      "
    >
      <template v-slot:toolbar>
        <b-row>
          <b-button variant="secondary" size="sm" @click="goBack" class="mr-2">
            <i class="flaticon2-back"></i> Quay lại
          </b-button>
        </b-row>
      </template>

      <template v-slot:preview>
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-2">Đang tải dữ liệu...</p>
        </div>
        <div v-else>
          <!-- Time conflict warning -->
          <b-alert
            v-if="timeConflict"
            show
            variant="warning"
            class="mb-4 light-warning-alert text-warning"
          >
            <i class="flaticon-warning mr-2 text-warning"></i>
            <strong>Cảnh báo:</strong> Đã có lịch lái thử xe
            <b-link
              @click="navigateToTestDrive(conflictDetails.id)"
              class="text-primary font-weight-bold test-drive-link"
            >
              #{{
                formatTestDriveCode(
                  conflictDetails.id,
                  conflictDetails.createdAt,
                )
              }}
            </b-link>
            vào khung giờ đã chọn:

            <strong
              >{{ formatDateTime(conflictDetails.startTime) }} -
              {{ formatDateTime(conflictDetails.endTime) }}</strong
            >.
          </b-alert>

          <b-alert
            v-if="previousTestDriveNotCompleted"
            show
            variant="warning"
            class="mb-4 light-warning-alert text-warning"
          >
            <i class="flaticon-warning mr-2 text-warning"></i>
            <strong>Cảnh báo:</strong> Phiếu lái thử trước đó cho xe này chưa
            được hoàn thành.
            <div v-if="previousTestDriveDetails" class="mt-1">
              <strong>Mã phiếu:</strong>
              <b-link
                @click="navigateToTestDrive(previousTestDriveDetails.id)"
                class="text-primary font-weight-bold test-drive-link"
              >
                #{{
                  formatTestDriveCode(
                    previousTestDriveDetails.id,
                    previousTestDriveDetails.createdAt,
                  )
                }}
              </b-link>
              <br />
              <strong>Thời gian:</strong>
              {{ formatDateTime(previousTestDriveDetails.startTime) }} -
              {{ formatDateTime(previousTestDriveDetails.endTime) }}<br />
              <strong>Trạng thái:</strong>
              {{ getStatusText(previousTestDriveDetails.status) }}
            </div>
          </b-alert>

          <b-alert
            v-if="shouldShowStatusWarning.show && isEditMode"
            show
            variant="warning"
            class="mb-4 light-warning-alert text-warning"
          >
            <i class="flaticon-warning mr-2 text-warning"></i>
            <strong>Thông báo:</strong> {{ shouldShowStatusWarning.message }}
          </b-alert>

          <b-tabs content-class="mt-3">
            <!-- Tab thông tin chính -->
            <b-tab title="Thông tin chính" active>
              <form @submit.prevent="saveTestDrive">
                <b-row class="mb-3">
                  <b-col md="9" class="d-flex align-items-center column-gap">
                    <b-form-group
                      label="Mã kho (Showroom) "
                      label-for="storeId"
                      class="required-control w-100"
                    >
                      <b-form-select
                        id="storeId"
                        v-model="testDriveForm.storeId"
                        :options="storeOptions"
                        :disabled="!canViewAll || isEditMode"
                        value-field="id"
                        text-field="name"
                        required
                        lazy
                      ></b-form-select>
                    </b-form-group>

                    <b-form-group
                      label="Mã sản phẩm"
                      label-for="productCode"
                      class="required-control w-100"
                    >
                      <b-input-group>
                        <b-form-input
                          id="productCode"
                          v-model="testDriveForm.productCode"
                          required
                          lazy
                          :disabled="isEditMode"
                          :readonly="!isEditMode"
                          @click="openDemoVehicleLookup"
                        ></b-form-input>
                        <b-input-group-append v-if="!isEditMode">
                          <b-button
                            variant="outline-secondary"
                            @click="openDemoVehicleLookup"
                            :disabled="
                              !canSearchDemoVehicles || !areFieldsEditable
                            "
                            class="btn-icon"
                          >
                            <i class="flaticon2-search-1"></i>
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>
                  </b-col>
                  <b-col md="3">
                    <b-form-group
                      label="Ngày đăng ký "
                      label-for="registrationDate"
                      class="required-control"
                    >
                      <date-picker
                        id="registrationDate"
                        placeholder="Chọn ngày"
                        class="form-control"
                        v-model="testDriveForm.registrationDate"
                        :config="configs.date"
                        :disabled="!areFieldsEditable"
                        required
                      >
                      </date-picker>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Thay thế các b-row chứa các trường Mã sản phẩm, Số khung, Biển số xe, Số km trước/sau khi đi và Lý do sử dụng -->
                <b-row class="mb-3">
                  <b-col md="12">
                    <b-form-group
                      label="Thông tin xe lái thử"
                      class="required-control"
                    >
                      <b-table-simple responsive small bordered>
                        <b-thead>
                          <b-tr>
                            <b-th>Số khung</b-th>
                            <b-th>Biển số xe</b-th>

                            <b-th>Số km trước khi đi</b-th>
                            <b-th>Số km sau khi đi</b-th>
                            <b-th>Trạng thái</b-th>
                            <b-th v-if="!isEditMode"></b-th>
                          </b-tr>
                        </b-thead>
                        <b-tbody>
                          <b-tr v-if="!!testDriveForm.chassisNumber">
                            <b-td>
                              <b-form-input
                                id="chassisNumber"
                                v-model="testDriveForm.chassisNumber"
                                required
                                disabled
                              ></b-form-input>
                            </b-td>
                            <b-td>
                              <b-form-input
                                id="licensePlate"
                                v-model="testDriveForm.licensePlate"
                                required
                                disabled
                              ></b-form-input>
                            </b-td>

                            <b-td>
                              <b-form-input
                                id="kmBeforeDrive"
                                type="number"
                                min="0"
                                step="0.1"
                                v-model="testDriveForm.kmBeforeDrive"
                                disabled
                                :required="
                                  isEditMode &&
                                  testDriveForm.status >=
                                    TEST_DRIVE_STATUS.REGISTERED
                                "
                              ></b-form-input>
                            </b-td>
                            <b-td>
                              <b-form-input
                                id="kmAfterDrive"
                                type="number"
                                min="0"
                                step="0.1"
                                v-model="testDriveForm.kmAfterDrive"
                                :disabled="
                                  !areFieldsEditable ||
                                  testDriveForm.status <
                                    TEST_DRIVE_STATUS.COMPLETED
                                "
                                :required="
                                  testDriveForm.status ===
                                  TEST_DRIVE_STATUS.COMPLETED
                                "
                              ></b-form-input>

                              <b-form-invalid-feedback
                                :state="isValidKmAfterDrive"
                                class="d-block"
                              >
                                <span
                                  v-if="
                                    testDriveForm.kmAfterDrive !== null &&
                                    !isValidKmAfterDrive
                                  "
                                >
                                  Số km sau khi đi phải lớn hơn số km trước khi
                                  đi
                                </span>
                              </b-form-invalid-feedback>
                            </b-td>
                            <b-td>
                              <b-form-select
                                id="status"
                                v-model="testDriveForm.status"
                                :options="availableStatusOptions"
                                :disabled="!canChangeStatus"
                                value-field="id"
                                text-field="text"
                              ></b-form-select>
                            </b-td>
                            <b-td v-if="!isEditMode">
                              <b-button
                                variant="link-danger"
                                size="sm"
                                v-if="testDriveForm.productCode && !isEditMode"
                                @click="clearProductCode"
                                title="Xóa mã sản phẩm"
                                class="btn-icon"
                              >
                                <i class="flaticon2-trash"></i>
                              </b-button>
                            </b-td>
                          </b-tr>
                        </b-tbody>
                      </b-table-simple>
                      <b-form-invalid-feedback
                        :state="!!testDriveForm.productCode"
                        class="d-block ml-2 mb-2"
                        v-if="!testDriveForm.productCode"
                      >
                        Vui lòng chọn mã sản phẩm trước !
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col md="6" class="d-flex align-items-center column-gap">
                    <b-form-group
                      label="Người sử dụng "
                      label-for="employeeId"
                      class="required-control w-100"
                    >
                      <auto-suggest
                        :model.sync="employeeSearchText"
                        :suggestions="employeeSuggestions"
                        :placeholder="'Người sử dụng'"
                        :suggestion-name="'displayName'"
                        :disabled="!areFieldsEditable"
                        :showFeedback="false"
                        @change="onEmployeeSearch"
                        @select="selectEmployee"
                      >
                      </auto-suggest>
                    </b-form-group>

                    <b-form-group
                      label="Khách hàng "
                      label-for="customerId"
                      class="w-100"
                    >
                      <b-row no-gutters>
                        <b-col>
                          <auto-suggest
                            :model.sync="customerSearchText"
                            :suggestions="customerSuggestions"
                            :placeholder="'Khách hàng'"
                            :suggestion-name="'displayName'"
                            :disabled="!areFieldsEditable"
                            :showFeedback="false"
                            @change="onCustomerSearch"
                            @select="selectCustomer"
                          >
                          </auto-suggest>
                        </b-col>
                        <b-col cols="auto" class="pl-0">
                          <b-button
                            variant="outline-primary"
                            :disabled="!areFieldsEditable"
                            @click="openCreateCustomerModal"
                            v-b-tooltip.hover
                            title="Thêm khách hàng mới"
                            size="sm"
                            class="btn-icon"
                          >
                            <i class="flaticon2-add-1"></i>
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col md="6" class="d-flex align-items-center column-gap">
                    <b-form-group
                      label="Lý do sử dụng "
                      label-for="reason"
                      class="required-control w-100"
                    >
                      <b-form-select
                        id="reason"
                        v-model="testDriveForm.reason"
                        :options="reasonOptions"
                        :disabled="!areFieldsEditable"
                        value-field="value"
                        text-field="label"
                        required
                      ></b-form-select>
                    </b-form-group>

                    <b-form-group
                      label="Mục đích "
                      label-for="purpose"
                      class="required-control w-100"
                    >
                      <b-form-input
                        id="purpose"
                        v-model="testDriveForm.purpose"
                        :disabled="!areFieldsEditable"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col md="6" class="d-flex align-items-center column-gap">
                    <b-form-group
                      label="Thời gian bắt đầu "
                      label-for="startTime"
                      class="required-control w-100"
                    >
                      <date-picker
                        id="startTime"
                        placeholder="Chọn thời gian"
                        class="form-control"
                        v-model="testDriveForm.startTime"
                        :config="configs.datetime"
                        :disabled="!areFieldsEditable"
                        required
                      >
                      </date-picker>
                    </b-form-group>

                    <b-form-group
                      label="Thời gian kết thúc "
                      label-for="endTime"
                      class="required-control w-100"
                    >
                      <date-picker
                        id="endTime"
                        placeholder="Chọn thời gian"
                        class="form-control"
                        v-model="testDriveForm.endTime"
                        :config="configs.datetime"
                        :disabled="!areFieldsEditable"
                        required
                      >
                      </date-picker>
                      <b-form-invalid-feedback :state="isValidTimeRange">
                        Thời gian kết thúc phải sau thời gian bắt đầu ít nhất 15
                        phút
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>

                  <b-col md="6"> </b-col>
                </b-row>

                <b-row class="mb-3">
                  <b-col md="6">
                    <b-form-group
                      label="Địa chỉ lái thử"
                      label-for="testDriveAddress"
                    >
                      <b-form-input
                        id="testDriveAddress"
                        v-model="testDriveForm.testDriveAddress"
                        :disabled="!areFieldsEditable"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="d-flex justify-content-end mt-4">
                  <b-button variant="secondary" class="mr-2" @click="goBack">
                    Hủy
                  </b-button>
                  <b-button
                    variant="primary"
                    type="submit"
                    :disabled="isSubmitting || !isFormValid || !canSave"
                  >
                    <b-spinner
                      small
                      v-if="isSubmitting"
                      class="mr-1"
                    ></b-spinner>
                    {{ isEditMode ? 'Cập nhật' : 'Lưu' }}
                  </b-button>
                </div>
              </form>
            </b-tab>

            <b-tab title="Tệp đính kèm" :disabled="!isEditMode">
              <attachments-tab
                v-if="isEditMode"
                :test-drive-id="testDriveForm.id"
                :are-fields-editable="areFieldsEditable && canUpdate"
              />
            </b-tab>
          </b-tabs>
        </div>
      </template>
    </KTCodePreview>

    <demo-vehicle-lookup-modal
      ref="demoVehicleLookupModal"
      :store-id="testDriveForm.storeId"
      @select="onDemoVehicleSelected"
      @hidden="onDemoVehicleLookupClosed"
      @error="handleError"
    />

    <create-customer-modal
      ref="createCustomerModal"
      @created="handleCustomerCreated"
      @hidden="resetNewCustomerForm"
      @error="handleError"
    />
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import AutoSuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import DemoVehicleLookupModal from './components/DemoVehicleLookupModal.vue';
import CreateCustomerModal from './components/CreateCustomerModal.vue';
import AttachmentsTab from './components/AttachmentsTab.vue';

import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { mapActions } from 'vuex';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';

import { TestDriveModel } from '../../posm/models/TestDrive.model';
import {
  DP_CONFIG,
  formatDate,
  formatDateTime,
  formatDatetimeDB,
  formatDBDate,
  getCurrentDate,
  YEAR_MONTH_DAY_DATETIME_FORMAT,
} from '../../../../utils/date';
import { getCurrentUser } from '../../../../utils/currentUser';
import { handleApiError } from '../../../../utils/errorHandler';
import localData from '@/utils/saveDataToLocal';
import { PERMISSIONS } from '../../../../utils/enum';
import moment from 'moment';
import ApiService from '../../../../core/services/api.service';
import { TestDriveService } from '../../../../core/services/test-drives';

// Constants
const SEARCH_DEBOUNCE_DELAY = 400;
const MIN_SEARCH_LENGTH = 2;
const TIME_DIFFERENCE_MINUTES = 14;

export default {
  name: 'TestDriveDetail',

  components: {
    KTCodePreview,
    AutoSuggest,
    DemoVehicleLookupModal,
    CreateCustomerModal,
    AttachmentsTab,
  },

  data() {
    return {
      // Loading and submission states
      isLoading: false,
      isSubmitting: false,

      // Mode states
      isEditMode: false,
      isViewMode: false,

      // Conflict test drive
      timeConflict: false,
      conflictDetails: this.createConflictDetails(),
      previousTestDriveNotCompleted: false,
      previousTestDriveDetails: this.createPreviousTestDriveDetails(),

      // Status warning
      statusWarning: false,
      statusWarningMessage: '',
      suggestedStatus: null,
      suggestedStatusText: '',

      // User context
      currentUser: getCurrentUser(),

      // Constants
      TEST_DRIVE_STATUS: TestDriveModel.STATUS,

      // Form data
      testDriveForm: new TestDriveModel(),
      originalStatus: null,

      // Lookup functionality
      employeeSearchText: '',
      employeeSuggestions: [],
      employeeSearchTimeout: null,

      customerSearchText: '',
      customerSuggestions: [],
      customerSearchTimeout: null,

      // Configs
      configs: {
        date: DP_CONFIG.date,
        datetime: {
          format: YEAR_MONTH_DAY_DATETIME_FORMAT,
          useCurrent: false,
          showClear: true,
          showClose: true,
          minDate: false,
          maxDate: false,
          sideBySide: false,
          locale: 'vi',
        },
      },

      // Options
      statusOptions: TestDriveModel.getStatusOptions(),
      reasonOptions: [],
      storeOptions: [],
    };
  },

  computed: {
    // Page title based on mode
    pageTitle() {
      return this.isEditMode
        ? 'Cập nhật đăng ký lái thử'
        : 'Thêm mới đăng ký lái thử';
    },

    // Permission checks
    canViewAll() {
      return this.checkPermission(PERMISSIONS.TEST_DRIVE_VIEW_ALL);
    },

    canView() {
      return (
        this.canViewAll || this.checkPermission(PERMISSIONS.TEST_DRIVE_VIEW)
      );
    },

    canInsert() {
      return this.checkPermission(PERMISSIONS.TEST_DRIVE_INSERT);
    },

    canUpdate() {
      return this.checkPermission(PERMISSIONS.TEST_DRIVE_UPDATE);
    },

    canDelete() {
      return this.checkPermission(PERMISSIONS.TEST_DRIVE_DELETE);
    },

    canChangeStatusAll() {
      return this.checkPermission(PERMISSIONS.TEST_DRIVE_CHANGE_STATUS_ALL);
    },

    canSave() {
      return this.isEditMode ? this.canUpdate : this.canInsert;
    },

    canChangeStatus() {
      return this.canChangeStatusAll || this.canUpdate;
    },

    areFieldsEditable() {
      return (
        (!this.isEditMode && this.canInsert) ||
        (this.isEditMode &&
          this.canUpdate &&
          this.testDriveForm.status <= this.TEST_DRIVE_STATUS.COMPLETED)
      );
    },

    canSearchDemoVehicles() {
      return !this.isEditMode && this.canInsert;
    },

    // Form validation
    isFormValid() {
      return (
        this.validateBasicFields() &&
        this.validateTimeRange() &&
        this.validateKilometers() &&
        this.validateConflicts()
      );
    },

    isValidTimeRange() {
      if (!this.testDriveForm.startTime || !this.testDriveForm.endTime) {
        return true;
      }

      const startTime = moment(
        this.testDriveForm.startTime,
        YEAR_MONTH_DAY_DATETIME_FORMAT,
      );
      const endTime = moment(
        this.testDriveForm.endTime,
        YEAR_MONTH_DAY_DATETIME_FORMAT,
      );

      return endTime.isAfter(startTime.add(TIME_DIFFERENCE_MINUTES, 'minutes'));
    },

    isValidKmAfterDrive() {
      const { kmAfterDrive, kmBeforeDrive } = this.testDriveForm;

      if (!kmAfterDrive) {
        return true;
      }

      return Number.parseFloat(kmAfterDrive) > Number.parseFloat(kmBeforeDrive);
    },

    availableStatusOptions() {
      if (!this.testDriveForm.status || !this.isEditMode) {
        return TestDriveModel.getStatusOptions();
      }

      const availableStatuses = TestDriveModel.getAvailableNextStatuses(
        this.testDriveForm.status,
      );
      const currentStatus = TestDriveModel.getStatusById(
        this.testDriveForm.status,
      );
      const allOptions = [currentStatus, ...availableStatuses];

      return this.removeDuplicateStatuses(allOptions);
    },

    shouldShowStatusWarning() {
      if (!this.testDriveForm.endTime || !this.testDriveForm.startTime) {
        return false;
      }

      const now = moment();
      const endTime = formatDatetimeDB(this.testDriveForm.endTime);
      const startTime = formatDatetimeDB(this.testDriveForm.startTime);

      // Kiểm tra nếu phiếu ở trạng thái Xác nhận và đã quá thời gian kết thúc
      if (
        this.testDriveForm.status === this.TEST_DRIVE_STATUS.CONFIRMED &&
        now.isAfter(endTime)
      ) {
        return {
          show: true,
          message: `Vui lòng chuyển trạng thái phiếu sang Hoàn thành`,
          suggestedStatus: this.TEST_DRIVE_STATUS.COMPLETED,
          suggestedStatusText: 'Hoàn thành',
        };
      }

      // Kiểm tra nếu phiếu ở trạng thái Đăng ký và đã đến thời gian bắt đầu
      if (
        this.testDriveForm.status === this.TEST_DRIVE_STATUS.REGISTERED &&
        now.isAfter(startTime)
      ) {
        return {
          show: true,
          message: `Vui lòng chuyển trạng thái phiếu sang Xác nhận`,
          suggestedStatus: this.TEST_DRIVE_STATUS.CONFIRMED,
          suggestedStatusText: 'Xác nhận',
        };
      }

      return { show: false };
    },
  },

  async created() {
    await this.initializeComponent();
  },

  mounted() {
    this.validateUserPermissions();
    this.setBreadcrumb();
  },

  methods: {
    ...mapActions({
      fetchTestDriveById: 'testDrives/fetchTestDriveById',
      createTestDrive: 'testDrives/createTestDrive',
      updateTestDrive: 'testDrives/updateTestDrive',
      fetchReasons: 'testDrives/fetchReasons',
      searchCustomers: 'customers/smartSearch',
      searchEmployees: 'users/smartSearch',
    }),

    // Utility methods
    formatDate,
    formatDateTime,
    formatDatetimeDB,
    formatDBDate,

    // Factory methods for initial data
    createConflictDetails() {
      return {
        startTime: null,
        endTime: null,
        id: null,
        createdAt: null,
      };
    },

    createPreviousTestDriveDetails() {
      return {
        id: null,
        startTime: '',
        endTime: '',
        status: '',
        createdAt: null,
      };
    },

    // Permission utilities
    checkPermission(permission) {
      return localData.checkPermission([permission]);
    },

    validateUserPermissions() {
      if (!this.canView) {
        makeToastFaile('Bạn không có quyền xem chi tiết đăng ký lái thử');
        this.goBack();
        return;
      }

      if (this.isEditMode && !(this.canUpdate || this.canInsert)) {
        makeToastFaile('Bạn không có quyền thêm/chỉnh sửa đăng ký lái thử');
        this.goBack();
        return;
      }
    },

    // Validation methods
    validateBasicFields() {
      const form = this.testDriveForm;
      const requiredFields = [
        'storeId',
        'registrationDate',
        'demoTicketId',
        'chassisNumber',
        'licensePlate',
        'employeeId',
        'reason',
        'purpose',
        'startTime',
        'endTime',
      ];

      return requiredFields.every((field) => form[field]);
    },

    validateTimeRange() {
      return this.isValidTimeRange;
    },

    validateKilometers() {
      const form = this.testDriveForm;

      if (
        form.status >= this.TEST_DRIVE_STATUS.CONFIRMED &&
        form.kmBeforeDrive === null
      ) {
        return false;
      }

      if (form.kmAfterDrive !== null && !this.isValidKmAfterDrive) {
        return false;
      }

      if (
        form.status === this.TEST_DRIVE_STATUS.CANCELLED &&
        this.isValidKmAfterDrive === null
      ) {
        return false;
      }

      return true;
    },

    validateConflicts() {
      return !this.timeConflict && !this.previousTestDriveNotCompleted;
    },

    removeDuplicateStatuses(statuses) {
      return statuses
        .filter(
          (status, index, self) =>
            self.findIndex((s) => s.id === status.id) === index,
        )
        .map((status) => ({
          id: status.id,
          text: status.text,
        }));
    },

    // Initialization
    async initializeComponent() {
      this.isLoading = true;

      try {
        this.setDefaultFormValues();
        await this.loadInitialData();
        await this.handleRouteMode();
        await this.loadStoreOptions();
      } catch (error) {
        handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },

    setDefaultFormValues() {
      this.testDriveForm.storeId = this.currentUser.storeId;
      this.testDriveForm.registrationDate = getCurrentDate();
      this.testDriveForm.employeeId = this.currentUser.id;
      this.employeeSearchText =
        this.currentUser.fullName || this.currentUser.name;
    },

    async loadInitialData() {
      await this.loadReasons();
    },

    async handleRouteMode() {
      const { id } = this.$route.query;

      if (id) {
        this.isEditMode = true;

        if (!this.canUpdate) {
          this.isViewMode = true;
        }

        await this.loadTestDrive(id);

        if (this.testDriveForm.status === this.TEST_DRIVE_STATUS.COMPLETED) {
          this.storeOptions.shift();
        }
      } else {
        if (!this.canInsert) {
          makeToastFaile('Bạn không có quyền tạo mới đăng ký lái thử');
          this.goBack();
          return;
        }
      }
    },

    async loadStoreOptions() {
      if (this.canViewAll) {
        await this.fetchStores();
      } else {
        this.storeOptions = [
          {
            id: this.currentUser.storeId,
            name:
              this.testDriveForm?.store?.name ||
              this.currentUser?.storeName ||
              'Showroom hiện tại',
          },
        ];
      }
    },

    // Data loading
    async loadTestDrive(id) {
      try {
        const { status } = this.$route.query;
        this.testDriveForm = await this.fetchTestDriveById(id);

        this.validateCrossStoreAccess();
        this.populateSearchFields();
        this.updateStatusFromQuery(status);
      } catch (error) {
        makeToastFaile('Không thể tải thông tin đăng ký lái thử');
        this.goBack();
      }
    },

    validateCrossStoreAccess() {
      if (
        !this.canViewAll &&
        this.testDriveForm.storeId !== this.currentUser.storeId
      ) {
        makeToastFaile(
          'Bạn không có quyền xem đăng ký lái thử của showroom khác',
        );
        this.goBack();
        return;
      }
    },

    populateSearchFields() {
      if (this.testDriveForm?.employee) {
        this.employeeSearchText = this.testDriveForm.employee.fullName;
      }

      if (this.testDriveForm?.customer) {
        this.customerSearchText = this.testDriveForm.customer.fullName;
      }
    },

    updateStatusFromQuery(status) {
      if (status && (this.canUpdate || this.canChangeStatusAll)) {
        this.testDriveForm.status = status;
      }
    },

    async loadReasons() {
      try {
        this.reasonOptions = await this.fetchReasons();
        if (this.reasonOptions.length > 0 && !this.testDriveForm.reason) {
          this.testDriveForm.reason = this.reasonOptions[0].value;
        }
      } catch (error) {
        handleApiError(error);
      }
    },

    async fetchStores() {
      try {
        const { data } = await ApiService.get('stores');
        this.storeOptions = data.data.data.map((store) => ({
          id: store.id,
          name: store.name,
        }));
      } catch (error) {
        handleApiError(error);
      }
    },

    // Conflict checking
    async checkTimeConflicts() {
      if (!this.shouldCheckTimeConflicts()) {
        this.timeConflict = false;
        return;
      }

      try {
        const response = await TestDriveService.checkTimeConflict({
          id: this.isEditMode ? this.testDriveForm.id : null,
          demoTicketId: this.testDriveForm.demoTicketId,
          startTime: formatDatetimeDB(this.testDriveForm.startTime),
          endTime: formatDatetimeDB(this.testDriveForm.endTime),
        });
        const conflictData = response.data.data;

        if (conflictData) {
          this.timeConflict = true;
          Object.assign(this.conflictDetails, conflictData);
        } else {
          this.timeConflict = false;
          Object.assign(this.conflictDetails, this.createConflictDetails());
        }
      } catch (error) {
        this.timeConflict = false;
        handleApiError(error);
      }
    },

    shouldCheckTimeConflicts() {
      return (
        this.testDriveForm.demoTicketId &&
        this.testDriveForm.startTime &&
        this.testDriveForm.endTime
      );
    },

    async checkPreviousTestDriveStatus() {
      if (!this.shouldCheckPreviousTestDrive()) {
        this.previousTestDriveNotCompleted = false;
        return;
      }

      try {
        const response = await TestDriveService.checkPreviousTestDriveStatus({
          id: this.isEditMode ? this.testDriveForm.id : null,
          demoTicketId: this.testDriveForm.demoTicketId,
          startTime: formatDatetimeDB(this.testDriveForm.startTime),
        });

        this.handlePreviousTestDriveResponse(response.data.data);
      } catch (error) {
        this.previousTestDriveNotCompleted = false;
        handleApiError(error);
      }
    },

    shouldCheckPreviousTestDrive() {
      return (
        this.testDriveForm.demoTicketId &&
        this.testDriveForm.startTime &&
        this.testDriveForm.status >= this.TEST_DRIVE_STATUS.COMPLETED
      );
    },

    handlePreviousTestDriveResponse(previousTestDriveData) {
      if (
        previousTestDriveData &&
        Number(previousTestDriveData.status) < this.TEST_DRIVE_STATUS.COMPLETED
      ) {
        this.previousTestDriveNotCompleted = true;
        Object.assign(this.previousTestDriveDetails, previousTestDriveData);
      } else {
        this.previousTestDriveNotCompleted = false;
        this.previousTestDriveDetails = null;
      }
    },

    // Form operations
    async saveTestDrive() {
      if (!this.isFormValid) {
        makeToastFaile('Vui lòng điền đầy đủ thông tin bắt buộc');
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = this.testDriveForm.toJSON();

        if (this.isEditMode) {
          await this.handleUpdateTestDrive(formData);
        } else {
          await this.handleCreateTestDrive(formData);
        }

        this.goBack();
      } catch (error) {
        handleApiError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async handleUpdateTestDrive(formData) {
      if (this.shouldBlockStatusChange()) {
        makeToastFaile('Bạn không có quyền thay đổi trạng thái này');
        return;
      }

      await this.updateTestDrive({
        id: this.testDriveForm.id,
        data: formData,
      });
      makeToastSuccess('Cập nhật đăng ký lái thử thành công');
    },

    async handleCreateTestDrive(formData) {
      await this.createTestDrive(formData);
      makeToastSuccess('Tạo đăng ký lái thử thành công');
    },

    shouldBlockStatusChange() {
      return (
        this.testDriveForm.status !== this.originalStatus &&
        !this.canChangeStatusAll &&
        this.testDriveForm.status === this.TEST_DRIVE_STATUS.COMPLETED
      );
    },

    // Navigation
    goBack() {
      this.$router.push({ name: 'test-drive.list' });
    },

    navigateToTestDrive(testDriveId) {
      if (!testDriveId) return;

      const routeData = this.$router.resolve({
        name: 'test-drive.edit',
        query: { id: testDriveId },
      });

      window.open(routeData.href, '_blank');
    },

    // Breadcrumb
    setBreadcrumb() {
      const breadcrumbItems = [
        { title: 'CRM', route: '/crm' },
        { title: 'Danh sách đăng ký lái thử', route: '/crm/test-drives' },
      ];

      if (this.isEditMode) {
        breadcrumbItems.push({
          title: this.isViewMode
            ? 'Xem chi tiết đăng ký lái thử'
            : 'Cập nhật đăng ký lái thử',
        });
      } else {
        breadcrumbItems.push({ title: 'Thêm mới đăng ký lái thử' });
      }

      this.$store.dispatch(SET_BREADCRUMB, breadcrumbItems);
    },

    // Utility methods
    formatTestDriveCode(uuid, createdAt = null) {
      return TestDriveModel.formatTestDriveCode(uuid, createdAt);
    },

    getStatusText(status) {
      return TestDriveModel.STATUS_MAP[status]?.text || 'N/A';
    },

    // Product code operations
    clearProductCode() {
      if (!this.canInsert) {
        return;
      }

      this.resetProductFields();
    },

    resetProductFields() {
      this.testDriveForm.productCode = '';
      this.testDriveForm.demoTicketId = '';
      this.testDriveForm.chassisNumber = '';
      this.testDriveForm.licensePlate = '';
      this.testDriveForm.kmBeforeDrive = '';
    },

    // Demo vehicle operations
    openDemoVehicleLookup() {
      if (this.isEditMode || !this.canInsert) {
        return;
      }
      this.$refs.demoVehicleLookupModal.show();
    },

    onDemoVehicleSelected(selectedVehicle) {
      if (!this.canInsert && !this.canUpdate) {
        return;
      }

      this.populateVehicleData(selectedVehicle);
      this.checkConflictsIfReady();
    },

    populateVehicleData(selectedVehicle) {
      this.testDriveForm.demoTicketId = selectedVehicle.id;
      this.testDriveForm.productCode = selectedVehicle.productCode;
      this.testDriveForm.chassisNumber =
        selectedVehicle?.productChassis.chassisNumber;
      this.testDriveForm.licensePlate = selectedVehicle.licensePlate;
      this.testDriveForm.kmBeforeDrive = selectedVehicle.currentKm;
    },

    checkConflictsIfReady() {
      if (this.testDriveForm.startTime && this.testDriveForm.endTime) {
        this.checkTimeConflicts();
      }
    },

    onDemoVehicleLookupClosed() {
      this.$refs.demoVehicleLookupModal.hide();
    },

    // Employee search
    async onEmployeeSearch(text) {
      if (!this.shouldLookup(text)) {
        this.employeeSuggestions = [];
        return;
      }

      this.debounceEmployeeSearch(text);
    },

    shouldLookup(text) {
      return (
        text &&
        text.length >= MIN_SEARCH_LENGTH &&
        (this.canInsert || this.canUpdate)
      );
    },

    debounceEmployeeSearch(text) {
      clearTimeout(this.employeeSearchTimeout);

      this.employeeSearchTimeout = setTimeout(async () => {
        await this.performEmployeeSearch(text);
      }, SEARCH_DEBOUNCE_DELAY);
    },

    async performEmployeeSearch(text) {
      try {
        const { data } = await this.searchEmployees({
          keyword: text,
          storeId: this.testDriveForm.storeId,
        });

        this.employeeSuggestions = data;
      } catch (error) {
        this.employeeSuggestions = [];
        handleApiError(error);
      }
    },

    selectEmployee(option) {
      if (!this.areFieldsEditable) {
        return;
      }

      if (option?.item) {
        this.testDriveForm.employeeId = option.item.id;
        this.employeeSearchText = option.item.fullName;
      } else {
        this.testDriveForm.employeeId = null;
        this.employeeSearchText = '';
      }
    },

    // Customer search
    onCustomerSearch(text) {
      if (!this.shouldLookup(text)) {
        this.customerSuggestions = [];
        return;
      }

      this.debounceCustomerSearch(text);
    },

    debounceCustomerSearch(text) {
      clearTimeout(this.customerSearchTimeout);

      this.customerSearchTimeout = setTimeout(async () => {
        await this.fetchCustomerDetails(text);
      }, SEARCH_DEBOUNCE_DELAY);
    },

    async fetchCustomerDetails(text) {
      try {
        const { data } = await this.searchCustomers({
          keyword: text,
          limit: 5,
        });

        this.customerSuggestions = data;
      } catch (error) {
        this.customerSuggestions = [];
        handleApiError(error);
      }
    },

    selectCustomer(option) {
      if (!this.areFieldsEditable) {
        return;
      }

      if (option?.item) {
        this.testDriveForm.customerId = option.item.id;
        this.customerSearchText = option.item.fullName;
      } else {
        this.testDriveForm.customerId = null;
        this.customerSearchText = '';
      }
    },

    // Customer modal
    openCreateCustomerModal() {
      if (!this.areFieldsEditable) {
        return;
      }

      this.$refs.createCustomerModal.show(this.customerSearchText);
    },

    handleCustomerCreated(customer) {
      if (!this.areFieldsEditable) {
        return;
      }

      this.testDriveForm.customerId = customer.id;
      this.customerSearchText = customer.fullName;
      makeToastSuccess('Tạo khách hàng mới thành công');
    },

    resetNewCustomerForm() {
      // Implementation can be added if needed
    },

    // Error handling
    handleError(message) {
      makeToastFaile(message);
    },

    // Cleanup
    beforeDestroy() {
      if (this.employeeSearchTimeout) {
        clearTimeout(this.employeeSearchTimeout);
      }
      if (this.customerSearchTimeout) {
        clearTimeout(this.customerSearchTimeout);
      }
    },
  },

  watch: {
    'testDriveForm.status': {
      immediate: true,
      handler(newValue) {
        if (this.isEditMode && !this.originalStatus) {
          this.originalStatus = newValue;
        }
        this.checkPreviousTestDriveStatus();
      },
    },

    'testDriveForm.startTime': {
      handler() {
        if (this.testDriveForm.demoTicketId && this.testDriveForm.endTime) {
          this.checkTimeConflicts();
        }
      },
    },

    'testDriveForm.endTime': {
      handler() {
        if (this.testDriveForm.demoTicketId && this.testDriveForm.startTime) {
          this.checkTimeConflicts();
        }
      },
    },
  },
};
</script>

<style scoped>
.required-control label::after {
  content: ' *';
  color: red;
}

.column-gap {
  column-gap: 10px;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.light-warning-alert {
  background-color: rgba(255, 243, 205, 0.7) !important;
  border-color: rgba(255, 193, 7, 0.5) !important;
}

.test-drive-detail {
  .test-drive-link {
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  .form-control:disabled {
    background-color: #f8f9fa;
    opacity: 1;
  }

  .table-simple {
    th,
    td {
      vertical-align: middle;
    }

    .btn-icon {
      width: 32px;
      height: 32px;
    }
  }

  .auto-suggest-container {
    position: relative;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .alert {
    border-radius: 0.375rem;

    .flaticon-warning {
      font-size: 1.1rem;
    }

    strong {
      font-weight: 600;
    }
  }

  .form-group {
    margin-bottom: 1rem;

    label {
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: #3f4254;
    }
  }

  .nav-tabs {
    border-bottom: 1px solid #ebedf3;

    .nav-link {
      color: #7e8299;
      border: none;
      padding: 0.75rem 1.5rem;

      &.active {
        color: #3699ff;
        border-bottom: 2px solid #3699ff;
        background-color: transparent;
      }

      &:hover:not(.active) {
        color: #3699ff;
        border-color: transparent;
      }

      &:disabled {
        color: #b5b5c3;
        cursor: not-allowed;
      }
    }
  }

  .tab-content {
    padding: 1.5rem 0;
  }

  .input-group {
    .btn {
      border-left: none;
    }

    .form-control:focus + .input-group-append .btn {
      border-color: #80bdff;
    }
  }

  .spinner-border-sm {
    width: 1rem;
    height: 1rem;
  }

  .text-center.py-5 {
    padding: 3rem 0;

    .spinner-border {
      width: 3rem;
      height: 3rem;
    }

    p {
      color: #7e8299;
      font-size: 0.9rem;
    }
  }

  .d-flex.justify-content-end {
    gap: 0.5rem;

    .btn {
      min-width: 100px;
    }
  }

  .column-gap {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    .column-gap {
      flex-direction: column;
      gap: 0;
    }

    .d-flex.justify-content-end {
      flex-direction: column-reverse;

      .btn {
        width: 100%;
        margin-bottom: 0.5rem;
        margin-right: 0 !important;
      }
    }

    .table-responsive {
      font-size: 0.875rem;

      th,
      td {
        padding: 0.5rem 0.25rem;
      }
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .tab-content {
    max-height: 70vh;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;

      &:hover {
        background: #a8a8a8;
      }
    }
  }

  .form-control:focus,
  .form-select:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  .is-invalid {
    .form-control,
    .form-select {
      border-color: #f64e60;

      &:focus {
        border-color: #f64e60;
        box-shadow: 0 0 0 0.2rem rgba(246, 78, 96, 0.25);
      }
    }
  }

  .is-valid {
    .form-control,
    .form-select {
      border-color: #1bc5bd;

      &:focus {
        border-color: #1bc5bd;
        box-shadow: 0 0 0 0.2rem rgba(27, 197, 189, 0.25);
      }
    }
  }

  .form-control:disabled,
  .form-select:disabled {
    background-color: #f3f6f9;
    border-color: #e4e6ea;
    color: #7e8299;
    cursor: not-allowed;
  }

  .btn {
    font-weight: 500;
    border-radius: 0.42rem;
    transition: all 0.15s ease-in-out;

    &:focus {
      box-shadow: none;
    }

    &.btn-primary {
      background-color: #3699ff;
      border-color: #3699ff;

      &:hover:not(:disabled) {
        background-color: #187de4;
        border-color: #187de4;
      }

      &:disabled {
        background-color: #b5b5c3;
        border-color: #b5b5c3;
        cursor: not-allowed;
      }
    }

    &.btn-secondary {
      background-color: #e4e6ea;
      border-color: #e4e6ea;
      color: #7e8299;

      &:hover:not(:disabled) {
        background-color: #d7dae7;
        border-color: #d7dae7;
        color: #5e6278;
      }
    }

    &.btn-outline-secondary {
      color: #7e8299;
      border-color: #e4e6ea;

      &:hover:not(:disabled) {
        background-color: #e4e6ea;
        border-color: #e4e6ea;
        color: #5e6278;
      }
    }

    &.btn-outline-primary {
      color: #3699ff;
      border-color: #3699ff;

      &:hover:not(:disabled) {
        background-color: #3699ff;
        border-color: #3699ff;
        color: #ffffff;
      }
    }

    &.btn-link-danger {
      color: #f64e60;
      background: none;
      border: none;
      padding: 0.25rem;

      &:hover:not(:disabled) {
        color: #ee2d41;
        background-color: rgba(246, 78, 96, 0.1);
      }
    }
  }

  .tooltip {
    font-size: 0.75rem;

    .tooltip-inner {
      background-color: #464e5f;
      border-radius: 0.25rem;
      padding: 0.25rem 0.5rem;
    }

    &.bs-tooltip-top .arrow::before {
      border-top-color: #464e5f;
    }

    &.bs-tooltip-bottom .arrow::before {
      border-bottom-color: #464e5f;
    }

    &.bs-tooltip-left .arrow::before {
      border-left-color: #464e5f;
    }

    &.bs-tooltip-right .arrow::before {
      border-right-color: #464e5f;
    }
  }
}
</style>
